import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Plus, Tag, Trash2, Edit2, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney as money } from '@/lib/adminFormat';
import { PromoForm } from '@/components/admin/PromoForm';

const today = () => new Date().toISOString().slice(0, 10);

const statusOf = (p) => {
  if (!p.active) return ['Désactivé', 'bg-zinc-100 text-zinc-500'];
  if (p.ends_at && today() > p.ends_at.slice(0, 10)) return ['Expiré', 'bg-red-50 text-red-600'];
  if (p.starts_at && today() < p.starts_at.slice(0, 10)) return ['Programmé', 'bg-sky-50 text-sky-700'];
  if (p.max_uses && p.uses >= p.max_uses) return ['Épuisé', 'bg-amber-50 text-amber-700'];
  return ['Actif', 'bg-emerald-50 text-emerald-700'];
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

export default function PromoCodes() {
  const [promos, setPromos] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get('/promo-codes'), api.get('/stores')]);
      setPromos(p.data || []); setStores(s.data || []);
    } catch { toast.error('Chargement impossible'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const storeName = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.name])), [stores]);
  const activeCount = promos.filter((p) => statusOf(p)[0] === 'Actif').length;

  const toggle = async (p) => {
    const { id, uses, discount_total, revenue_total, created_at, updated_at, workspace_id, ...body } = p;
    try { await api.put(`/promo-codes/${id}`, { ...body, active: !p.active }); load(); } catch { toast.error('Échec'); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    try { await api.delete(`/promo-codes/${p.id}`); toast.success('Code supprimé'); load(); } catch { toast.error('Échec'); }
  };
  const copy = (code) => { navigator.clipboard?.writeText(code).then(() => toast.success('Code copié'), () => {}); };

  return (
    <div className="space-y-6" data-testid="promo-codes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="dashboard-eyebrow">Ventes</div>
          <h1 data-testid="promo-codes-title">Codes promo</h1>
          <p className="dashboard-caption" data-testid="promo-codes-count">{loading ? 'Chargement…' : `${promos.length} code${promos.length > 1 ? 's' : ''} · ${activeCount} actif${activeCount > 1 ? 's' : ''}`}</p>
        </div>
        <Button data-testid="promo-new-button" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-2" /> Nouveau code</Button>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : promos.length === 0 ? (
        <Card className="p-10 text-center text-zinc-500" data-testid="promo-empty">
          <Tag className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
          Aucun code promo. Crée ton premier code pour tes boutiques.
        </Card>
      ) : (
        <Card className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="promo-table">
            <thead className="text-xs text-zinc-500 border-b border-zinc-200">
              <tr className="text-left">
                {['Code', 'Réduction', 'Boutiques', 'Règles', 'Utilisations', 'Réductions / CA', 'Statut', ''].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => {
                const [label, cls] = statusOf(p);
                const rules = [
                  p.min_amount ? `Min. ${money(p.min_amount)}` : '',
                  p.once_per_email ? '1× / client' : '',
                  p.auto_apply ? '⚡ Auto' : '',
                  p.starts_at || p.ends_at ? `${fmtDate(p.starts_at) || '…'} → ${fmtDate(p.ends_at) || '…'}` : '',
                ].filter(Boolean);
                return (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors" data-testid={`promo-row-${p.code}`}>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => copy(p.code)} data-testid={`promo-copy-${p.code}`} className="inline-flex items-center gap-1.5 font-mono font-semibold tracking-wider hover:text-zinc-500 transition-colors">{p.code}<Copy className="h-3 w-3" /></button>
                      {p.auto_apply && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 align-middle" data-testid={`promo-auto-badge-${p.code}`}>AUTO</span>}
                    </td>
                    <td className="px-4 py-3 font-medium">{p.type === 'percent' ? `−${p.value} %` : `−${money(p.value)}`}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600 max-w-[220px]">{(p.store_ids || []).map((id) => storeName[id] || '—').join(', ')}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{rules.length ? rules.join(' · ') : '—'}</td>
                    <td className="px-4 py-3 font-mono" data-testid={`promo-uses-${p.code}`}>{p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ''}</td>
                    <td className="px-4 py-3 text-xs"><span className="text-emerald-700">−{money(p.discount_total || 0)}</span><span className="text-zinc-400"> / {money(p.revenue_total || 0)}</span></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`} data-testid={`promo-status-${p.code}`}>{label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Switch checked={!!p.active} onCheckedChange={() => toggle(p)} data-testid={`promo-toggle-${p.code}`} />
                        <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`promo-edit-${p.code}`}><Edit2 className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`promo-delete-${p.code}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <PromoForm open={open} onClose={() => setOpen(false)} editing={editing} stores={stores} onSaved={load} />
    </div>
  );
}
